(() => {
  'use strict';

  const QUICK_ADD_SELECTOR = '[data-ssb-quick-add]';
  const STICKY_SELECTOR = '[data-ssb-sticky-purchase]';

  const announce = (message, state = 'success') => {
    let region = document.getElementById('SSBConversionStatus');
    if (!region) {
      region = document.createElement('div');
      region.id = 'SSBConversionStatus';
      region.className = 'ssb-conversion-status';
      document.body.appendChild(region);
    }

    region.setAttribute('role', state === 'error' ? 'alert' : 'status');
    region.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
    region.dataset.state = state;
    region.textContent = message;
    region.classList.add('is-visible');
    window.clearTimeout(region._ssbTimer);
    region._ssbTimer = window.setTimeout(() => region.classList.remove('is-visible'), 2800);
  };

  const labelNode = (button) => button?.querySelector('[data-ssb-button-label]') || button;

  const setButtonState = (button, state, text) => {
    if (!button) return;
    const label = labelNode(button);
    if (!button.dataset.ssbOriginalLabel) button.dataset.ssbOriginalLabel = label.textContent.trim();

    const busy = state === 'loading';
    button.disabled = busy || state === 'success';
    button.toggleAttribute('aria-busy', busy);

    if (state === 'loading') label.textContent = text || 'Adding…';
    else if (state === 'success') label.textContent = text || 'Added';
    else if (state === 'error') label.textContent = text || 'Try again';
    else label.textContent = button.dataset.ssbOriginalLabel || text || 'Add to cart';
  };

  const submitQuickAdd = async (form) => {
    if (form.dataset.ssbSubmitting === 'true') return;
    form.dataset.ssbSubmitting = 'true';
    let added = false;

    const button = form.querySelector('button[type="submit"]');
    setButtonState(button, 'loading');

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This piece could not be added.');
      }

      added = true;
      setButtonState(button, 'success');
      announce('Added to cart. Opening order review.');
      document.dispatchEvent(new CustomEvent('ssb:quick-add:success', {
        detail: { id: result.id || null, source: form.dataset.ssbSource || 'quick-add' },
      }));

      window.setTimeout(() => {
        window.location.assign(window.routes?.cart_url || '/cart');
      }, 350);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'This piece could not be added.';
      setButtonState(button, 'error');
      announce(message, 'error');
      document.dispatchEvent(new CustomEvent('ssb:quick-add:error', { detail: { message } }));
      window.setTimeout(() => setButtonState(button, 'idle'), 2200);
    } finally {
      // A successful add stays locked until navigation completes to avoid
      // accidental second additions in the 350ms transition window.
      if (!added) form.dataset.ssbSubmitting = 'false';
    }
  };

  const initQuickAdds = (scope = document) => {
    scope.querySelectorAll?.(QUICK_ADD_SELECTOR).forEach((form) => {
      if (form.dataset.ssbBound === 'true') return;
      form.dataset.ssbBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitQuickAdd(form);
      });
    });
  };

  const syncStickyBar = (bar) => {
    const formId = bar.dataset.formId;
    const productRoot = bar.closest('product-info') || document.querySelector('product-info');
    const productForm = formId ? document.getElementById(formId) : productRoot?.querySelector('form[data-type="add-to-cart-form"]');
    const sourceButton = productRoot?.querySelector('.product-form__submit');
    const stickyButton = bar.querySelector('[data-ssb-sticky-submit]');
    const priceContainer = productRoot?.querySelector('[id^="price-"] .price');
    const priceSource = priceContainer?.querySelector(priceContainer.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
    const stickyPrice = bar.querySelector('[data-ssb-sticky-price]');

    if (stickyPrice && priceSource && stickyPrice.textContent !== priceSource.textContent.trim()) stickyPrice.textContent = priceSource.textContent.trim();

    if (stickyButton && sourceButton) {
      stickyButton.disabled = sourceButton.disabled;
      stickyButton.setAttribute('aria-disabled', sourceButton.disabled ? 'true' : 'false');
      const sourceText = sourceButton.querySelector('span')?.textContent.trim() || 'Add to cart';
      const stickyText = stickyButton.querySelector('span');
      if (stickyText && stickyText.textContent !== sourceText) stickyText.textContent = sourceText;
    }

    if (stickyButton && stickyButton.dataset.ssbBound !== 'true') {
      stickyButton.dataset.ssbBound = 'true';
      stickyButton.addEventListener('click', () => {
        const currentRoot = bar.closest('product-info') || document.querySelector('product-info');
        const currentButton = currentRoot?.querySelector('.product-form__submit');
        if (currentButton && !currentButton.disabled) currentButton.click();
      });
    }

    if (!bar._ssbProductObserver && productRoot && 'MutationObserver' in window) {
      bar._ssbProductObserver = new MutationObserver((records) => {
        if (records.every((record) => bar.contains(record.target))) return;
        window.cancelAnimationFrame(bar._ssbSyncFrame);
        bar._ssbSyncFrame = window.requestAnimationFrame(() => syncStickyBar(bar));
      });
      bar._ssbProductObserver.observe(productRoot, { childList: true, subtree: true });
    }

    if (!bar.dataset.ssbVisibilityBound && sourceButton && 'IntersectionObserver' in window) {
      bar.dataset.ssbVisibilityBound = 'true';
      const observer = new IntersectionObserver(([entry]) => {
        const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', shouldShow);
        bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        bar.inert = !shouldShow;
      }, { threshold: 0.05 });
      observer.observe(sourceButton);
      bar._ssbVisibilityObserver = observer;
    }
  };

  const initStickyPurchases = (scope = document) => {
    scope.querySelectorAll?.(STICKY_SELECTOR).forEach(syncStickyBar);
  };

  const initCustomBuildCartLock = () => {
    if (document.documentElement.dataset.ssbCustomBuildCartLockBound === 'true') return;
    document.documentElement.dataset.ssbCustomBuildCartLockBound = 'true';

    document.addEventListener('click', async (event) => {
      const button = event.target.closest?.('[data-ssb-drawer-remove-build]');
      if (!button) return;

      event.preventDefault();
      if (button.disabled) return;
      if (!window.confirm('Remove this entire custom build from the cart?')) return;

      const keys = (button.getAttribute('data-build-keys') || '').split('||').map((key) => key.trim()).filter(Boolean);
      if (!keys.length) { announce('This build could not be identified. Open the cart to review it.', 'error'); return; }
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));

      button.disabled = true;
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates })
        });

        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'The build could not be removed.');
        window.location.reload();
      } catch (error) {
        console.error('SSB custom build removal failed:', error);
        button.disabled = false;
        announce(error.message || 'The build could not be removed. Please try again.', 'error');
      }
    });
  };

  const initialize = (scope = document) => {
    initQuickAdds(scope);
    initStickyPurchases(scope);
    initCustomBuildCartLock();
  };

  document.addEventListener('change', () => {
    window.requestAnimationFrame(() => initStickyPurchases(document));
  }, true);
  document.addEventListener('variant:change', () => initStickyPurchases(document));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target || document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})();
